import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Play, 
  Pause, 
  CheckCircle, 
  Clock, 
  Target, 
  Eye,
  MoreVertical,
  Download,
  Trash2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { WorkoutProgram } from '@/hooks/useWorkoutPrograms';
import { exportToPDF } from '@/lib/pdfExport';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { usePremium } from '@/hooks/usePremium';
import { usePremiumGate } from '@/contexts/PremiumGateContext';

interface WorkoutProgramCardProps {
  program: WorkoutProgram;
  onView: (program: WorkoutProgram) => void;
  onStatusUpdate: (programId: string, status: string) => void;
  onDelete: (programId: string) => void;
}

export const WorkoutProgramCard = ({
  program,
  onView,
  onStatusUpdate,
  onDelete
}: WorkoutProgramCardProps) => {
  const { t } = useTranslation();
  const { isPremium } = usePremium();
  const { openPaywall } = usePremiumGate();
  const [isExporting, setIsExporting] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'completed': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'paused': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      default: return 'bg-muted/10 text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <Play className="h-3 w-3" />;
      case 'completed': return <CheckCircle className="h-3 w-3" />;
      case 'paused': return <Pause className="h-3 w-3" />;
      default: return <Clock className="h-3 w-3" />;
    }
  };

  const getPrimaryAction = () => {
    switch (program.status) {
      case 'draft':
      case 'paused':
        return {
          label: t('components.workoutProgramCard.startProgram'),
          icon: <Play className="h-4 w-4 mr-2" />,
          action: () => onStatusUpdate(program.id, 'active')
        };
      case 'active':
        return {
          label: t('components.workoutProgramCard.pauseProgram'),
          icon: <Pause className="h-4 w-4 mr-2" />,
          action: () => onStatusUpdate(program.id, 'paused')
        };
      case 'completed':
        return {
          label: t('components.workoutProgramCard.restartProgram'),
          icon: <Play className="h-4 w-4 mr-2" />,
          action: () => onStatusUpdate(program.id, 'active')
        };
      default:
        return null;
    }
  };

  const handleExportPDF = async () => {
    if (!isPremium) {
      toast.error(t('premium.pdfExportLocked'));
      openPaywall({ description: t('premium.pdfExportLocked'), source: 'pdf_export' });
      return;
    }
    setIsExporting(true);
    try {
      await exportToPDF(program);
      toast.success(t('components.workoutProgramCard.toast.exportSuccess'));
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(t('components.workoutProgramCard.toast.exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm(t('components.workoutProgramCard.deleteConfirm'))) {
      onDelete(program.id);
    }
  };

  const primaryAction = getPrimaryAction();

  return (
    <Card className="hover:shadow-md transition-shadow border-0">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg font-semibold mb-2 truncate">
              {program.title}
            </CardTitle>
            <div className="flex items-center gap-3 text-sm text-muted-foreground mb-2">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {program.duration_weeks || t('components.workoutProgramCard.weeksCustom')} {t('components.workoutProgramCard.weeksUnit')}
              </div>
              <div className="flex items-center gap-1">
                <Target className="h-3 w-3" />
                {program.days.length} {t('components.workoutProgramCard.daysUnit')}
              </div>
              <Badge className={getStatusColor(program.status)}>
                {getStatusIcon(program.status)}
                {program.status}
              </Badge>
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="shrink-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onView(program)}>
                <Eye className="h-4 w-4 mr-2" />
                {t('components.workoutProgramCard.viewDetails')}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={handleExportPDF}
                disabled={isExporting}
              >
                <Download className="h-4 w-4 mr-2" />
                {isExporting ? t('components.workoutProgramCard.exporting') : t('components.workoutProgramCard.exportPdf')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleDelete}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t('components.workoutProgramCard.deleteProgram')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {program.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {program.description}
          </p>
        )}
      </CardHeader>

      <CardContent className="pt-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
          <div className="text-xs text-muted-foreground">
            {t('components.workoutProgramCard.createdAt', { date: new Date(program.created_at).toLocaleDateString() })}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onView(program)}
              className="text-xs sm:text-sm px-3 sm:px-4"
            >
              <Eye className="h-3 sm:h-4 w-3 sm:w-4 mr-1 sm:mr-2" />
              {t('components.workoutProgramCard.view')}
            </Button>
            
            {primaryAction && (
              <Button
                size="sm"
                onClick={primaryAction.action}
                className="bg-gradient-primary text-black text-xs sm:text-sm px-3 sm:px-4"
              >
                {primaryAction.icon}
                {primaryAction.label}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};